import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Key,
  Users,
  Buildings,
  ChartBar,
  Plus,
  Trash,
  Copy,
  CheckCircle,
  Clock,
  Warning,
  Crown,
  Rocket,
  CurrencyCircleDollar,
  Coins
} from '@phosphor-icons/react';

// Logo component
const FinanciALLogo = () => (
  <img 
    src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
    alt="FinanciAL" 
    className="w-9 h-9 rounded-lg object-cover"
  />
);

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [accessKeys, setAccessKeys] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newKeyForm, setNewKeyForm] = useState({ plan_tier: 'fortune', duration_months: 1, notes: '' });
  const [generatedKey, setGeneratedKey] = useState(null);

  useEffect(() => {
    checkAdminAndLoadData();
  }, []);

  const checkAdminAndLoadData = async () => {
    try {
      const adminCheck = await adminAPI.checkAdmin();
      if (!adminCheck.data.is_admin) {
        toast.error('Nuk keni qasje në panelin e administratorit');
        navigate('/dashboard');
        return;
      }
      setIsAdmin(true);
      
      // Load stats and keys in parallel
      const [statsRes, keysRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAccessKeys()
      ]);
      
      setStats(statsRes.data);
      setAccessKeys(keysRes.data.keys);
    } catch (error) {
      console.error('Admin check error:', error);
      toast.error('Nuk keni qasje në panelin e administratorit');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const response = await adminAPI.generateAccessKey(newKeyForm);
      setGeneratedKey(response.data.access_key);
      toast.success('Çelësi u krijua me sukses!');
      
      // Refresh keys list
      const keysRes = await adminAPI.getAccessKeys();
      setAccessKeys(keysRes.data.keys);
      
      // Refresh stats
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Generate key error:', error);
      toast.error(error.response?.data?.detail || 'Dështoi krijimi i çelësit');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Jeni të sigurt që doni të fshini këtë çelës?')) return;
    
    try {
      await adminAPI.deleteAccessKey(keyId);
      toast.success('Çelësi u fshi me sukses');
      setAccessKeys(accessKeys.filter(k => k.key_id !== keyId));
      
      // Refresh stats
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Delete key error:', error);
      toast.error(error.response?.data?.detail || 'Dështoi fshirja e çelësit');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Çelësi u kopjua!');
  };

  const getPlanIcon = (tier) => {
    const icons = { fortune: Rocket, enterprise: Crown };
    return icons[tier] || Key;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('sq-AL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A3C34] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-[#1A3C34]/10 bg-white/95 backdrop-blur">
        <div className="flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-[#1A3C34]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Kthehu te Paneli
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <FinanciALLogo />
            <span className="font-semibold text-lg text-[#1A3C34]">Admin Panel</span>
          </div>
          <Badge className="bg-[#D4A373] text-white">
            <Crown className="w-3 h-3 mr-1" weight="fill" />
            Administrator
          </Badge>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-[#1A3C34]/10" data-testid="admin-stats-users">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#1A3C34]/60">Përdorues Totalë</p>
                  <p className="text-2xl font-semibold text-[#1A3C34]">{stats?.total_users || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#1A3C34]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#1A3C34]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#1A3C34]/10" data-testid="admin-stats-companies">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#1A3C34]/60">Kompani Totale</p>
                  <p className="text-2xl font-semibold text-[#1A3C34]">{stats?.total_companies || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
                  <Buildings className="w-5 h-5 text-[#2A9D8F]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#1A3C34]/10" data-testid="admin-stats-available-keys">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#1A3C34]/60">Çelësa të Disponueshëm</p>
                  <p className="text-2xl font-semibold text-[#2A9D8F]">{stats?.available_keys || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#D4A373]/10 flex items-center justify-center">
                  <Key className="w-5 h-5 text-[#D4A373]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#1A3C34]/10" data-testid="admin-stats-used-keys">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#1A3C34]/60">Çelësa të Përdorur</p>
                  <p className="text-2xl font-semibold text-[#E76F51]">{stats?.used_keys || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#E76F51]/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#E76F51]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Breakdown */}
        <Card className="mb-8 border-[#1A3C34]/10">
          <CardHeader>
            <CardTitle className="text-[#1A3C34] flex items-center gap-2">
              <ChartBar className="w-5 h-5" />
              Ndarje sipas Planeve
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { tier: 'coin', name: 'Coin', icon: Coins, color: 'bg-gray-100 text-gray-600' },
                { tier: 'cashflow', name: 'Cashflow', icon: CurrencyCircleDollar, color: 'bg-blue-100 text-blue-600' },
                { tier: 'fortune', name: 'Fortune', icon: Rocket, color: 'bg-purple-100 text-purple-600' },
                { tier: 'enterprise', name: 'Enterprise', icon: Crown, color: 'bg-amber-100 text-amber-600' }
              ].map(({ tier, name, icon: Icon, color }) => (
                <div key={tier} className={`rounded-xl p-4 ${color.split(' ')[0]}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${color.split(' ')[1]}`} weight="fill" />
                    <span className={`font-medium ${color.split(' ')[1]}`}>{name}</span>
                  </div>
                  <p className={`text-2xl font-bold ${color.split(' ')[1]}`}>
                    {stats?.subscription_tiers?.[tier] || 0}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Access Keys Management */}
        <Card className="border-[#1A3C34]/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#1A3C34] flex items-center gap-2">
              <Key className="w-5 h-5" />
              Menaxhimi i Çelësave të Aksesit
            </CardTitle>
            <Button 
              onClick={() => { setShowGenerateModal(true); setGeneratedKey(null); }}
              className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
              data-testid="admin-generate-key-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Gjenero Çelës
            </Button>
          </CardHeader>
          <CardContent>
            {accessKeys.length === 0 ? (
              <div className="text-center py-12">
                <Key className="w-12 h-12 text-[#1A3C34]/30 mx-auto mb-4" />
                <p className="text-[#1A3C34]/60">Nuk ka çelësa të krijuar ende</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F8F9FA]">
                    <tr>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Çelësi</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Plani</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Kohëzgjatja</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Statusi</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Përdorur nga</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Krijuar</th>
                      <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Veprime</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1A3C34]/5">
                    {accessKeys.map(key => {
                      const PlanIcon = getPlanIcon(key.plan_tier);
                      return (
                        <tr key={key.key_id} className="hover:bg-[#F8F9FA]">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <code className="px-2 py-1 bg-[#1A3C34]/5 rounded text-sm font-mono">
                                {key.access_key}
                              </code>
                              <button 
                                onClick={() => copyToClipboard(key.access_key)}
                                className="p-1 hover:bg-[#1A3C34]/10 rounded"
                              >
                                <Copy className="w-4 h-4 text-[#1A3C34]/60" />
                              </button>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <PlanIcon className="w-4 h-4 text-[#D4A373]" weight="fill" />
                              <span className="capitalize">{key.plan_tier}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            {key.duration_months === 1 ? '1 Muaj' : '12 Muaj'}
                          </td>
                          <td className="p-4">
                            {key.is_used ? (
                              <Badge className="bg-[#E76F51]/10 text-[#E76F51]">
                                <CheckCircle className="w-3 h-3 mr-1" weight="fill" />
                                Përdorur
                              </Badge>
                            ) : (
                              <Badge className="bg-[#2A9D8F]/10 text-[#2A9D8F]">
                                <Clock className="w-3 h-3 mr-1" />
                                Disponueshëm
                              </Badge>
                            )}
                          </td>
                          <td className="p-4 text-sm text-[#1A3C34]/70">
                            {key.used_by_email || '-'}
                          </td>
                          <td className="p-4 text-sm text-[#1A3C34]/60">
                            {formatDate(key.created_at)}
                          </td>
                          <td className="p-4 text-right">
                            {!key.is_used && (
                              <button
                                onClick={() => handleDeleteKey(key.key_id)}
                                className="p-2 hover:bg-[#E76F51]/10 rounded-lg text-[#E76F51]"
                              >
                                <Trash className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Generate Key Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1A3C34]">Gjenero Çelës të Ri</DialogTitle>
          </DialogHeader>
          
          {generatedKey ? (
            <div className="space-y-4">
              <div className="bg-[#2A9D8F]/10 rounded-xl p-4 text-center">
                <CheckCircle className="w-12 h-12 text-[#2A9D8F] mx-auto mb-2" weight="fill" />
                <p className="text-[#1A3C34] font-medium mb-4">Çelësi u krijua me sukses!</p>
                <div className="bg-white rounded-lg p-4 border-2 border-dashed border-[#1A3C34]/20">
                  <code className="text-lg font-mono font-bold text-[#1A3C34]">{generatedKey}</code>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => copyToClipboard(generatedKey)}
                  className="flex-1 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Kopjo Çelësin
                </Button>
                <Button
                  onClick={() => { setShowGenerateModal(false); setGeneratedKey(null); }}
                  variant="outline"
                  className="flex-1"
                >
                  Mbyll
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-[#1A3C34]">Plani</Label>
                <Select
                  value={newKeyForm.plan_tier}
                  onValueChange={(v) => setNewKeyForm({...newKeyForm, plan_tier: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fortune">
                      <div className="flex items-center gap-2">
                        <Rocket className="w-4 h-4 text-[#D4A373]" />
                        Fortune ($79/muaj)
                      </div>
                    </SelectItem>
                    <SelectItem value="enterprise">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-[#D4A373]" />
                        Enterprise ($199/muaj)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-[#1A3C34]">Kohëzgjatja</Label>
                <Select
                  value={String(newKeyForm.duration_months)}
                  onValueChange={(v) => setNewKeyForm({...newKeyForm, duration_months: parseInt(v)})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Muaj</SelectItem>
                    <SelectItem value="12">12 Muaj (Vjetor)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-[#1A3C34]">Shënime (opsionale)</Label>
                <Input
                  value={newKeyForm.notes}
                  onChange={(e) => setNewKeyForm({...newKeyForm, notes: e.target.value})}
                  placeholder="p.sh. Për klientin X..."
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleGenerateKey}
                  disabled={generating}
                  className="flex-1 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
                >
                  {generating ? 'Duke gjeneruar...' : 'Gjenero Çelësin'}
                </Button>
                <Button
                  onClick={() => setShowGenerateModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Anulo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
